/**
 * Patch model metadata to compensate for missing/incorrect server values.
 * Each patch is a workaround — remove it when the server provides the correct metadata.
 *
 * @param {string} modelName - The model name (e.g. 'datasets', 'experiments')
 * @param {string} modelType - The model type (e.g. 'transmissionemassay', 'DEFAULT')
 * @param {Object} attributes - The combined model+view attributes dict (mutated in place)
 */
export function patchModelMeta(modelName, modelType, attributes) {
  // TODO-SERVER-010: id should have visible: true from server
  if (attributes.id) {
    attributes.id = { ...attributes.id, visible: true }
  }
  // TODO-SERVER-011: permissions should have visible: true from server
  if (attributes.permissions) {
    attributes.permissions = { ...attributes.permissions, visible: true }
  }

  // TODO-SERVER-001: owned_by should be readonly on data models
  if (['datasets', 'datafiles', 'datafilecopies'].includes(modelName)) {
    if (attributes.owned_by) {
      attributes.owned_by = { ...attributes.owned_by, readonly: true }
    }
  }

  // TODO-SERVER-002: isStorageEquipment should come from server widget.arguments
  if (modelName === 'storageequipment') {
    patchWidgetArgs(attributes, 'stock_locations', 'edit', {
      isStorageEquipment: true,
    })
  }

  // TODO-SERVER-008: precision should come from server field metadata
  if (attributes.price) {
    attributes.price = { ...attributes.price, precision: 2 }
  }
  if (attributes.total_price) {
    attributes.total_price = { ...attributes.total_price, precision: 2 }
  }

  // TODO-SERVER-016: nr_of_samples should be mandatory and numeric on assay models
  if (attributes.nr_of_samples) {
    attributes.nr_of_samples = {
      ...attributes.nr_of_samples,
      mandatory: true,
      type: 'number',
      validations: [
        ...(attributes.nr_of_samples.validations || []),
        { name: 'required', error_message: 'This field is mandatory.' },
      ],
    }
  }

  // TODO-SERVER-017: z_stack / timeLapse should be server-defined virtual attributes
  // on imaging assay models, so the UI doesn't need synthetic entries.
  if (attributes.z_planes && !attributes.z_stack) {
    attributes.z_stack = { label: 'Z Stack', mandatory: true }
  }
  if (attributes.time_points && !attributes.timeLapse) {
    attributes.timeLapse = { label: 'Time Lapse', mandatory: true }
  }

  // TODO-SERVER-017b: order field missing from view.detail.attributes on ordereditem model
  if (modelName === 'ordereditems' && attributes.order) {
    attributes.order = {
      ...attributes.order,
      label: 'Order',
      visible: true,
      order: -1,
      widget: { view: { type: 'LinkedItem' } },
    }
  }

  // TODO-SERVER-018: ordereditems should have widget.type='OrderedItems' from server
  // TODO-SERVER-018b: ordereditems list widget should come from server as widget.list
  if (attributes.ordereditems) {
    attributes.ordereditems = {
      ...attributes.ordereditems,
      widget: {
        edit: { type: 'OrderedItems' },
        view: { type: 'OrderedItems' },
        list: { type: 'LinkedItem' },
      },
    }
  }

  // TODO-SERVER-025: task_id copyable should come from server field metadata
  if (modelName === 'usertasks' && attributes.task_id) {
    attributes.task_id = { ...attributes.task_id, copyable: true }
  }

  // TODO-SERVER-019: instrument field labels should be type-specific from server metadata
  patchInstrumentLabels(modelType, attributes)

  // TODO-SERVER-020: run_dir copyable should come from server field metadata
  if (attributes.run_dir) {
    attributes.run_dir = { ...attributes.run_dir, copyable: true }
  }

  // TODO-SERVER-023: name (URI) copyField/copyLabel should come from server widget.arguments
  if (modelName === 'datafilecopies' && attributes.name) {
    patchWidgetArgs(attributes, 'name', 'view', {
      copyField: 'name',
      copyLabel: 'URI',
    })
  }

  // TODO-SERVER-024: datafilecopies copyLabel should come from server widget.arguments
  if (modelName === 'datafiles' && attributes.datafilecopies) {
    patchWidgetArgs(attributes, 'datafilecopies', 'view', {
      copyField: 'name',
      copyLabel: 'URI',
    })
  }

  // TODO-SERVER-025: default_category needs compound widget + mandatory flag on ontologies
  if (modelName === 'ontologies' && attributes.default_category) {
    attributes.default_category = {
      ...attributes.default_category,
      mandatory: true,
      validations: [
        ...(attributes.default_category.validations || []),
        { name: 'required', error_message: 'Default category is mandatory.' },
      ],
      widget: {
        ...attributes.default_category.widget,
        edit: { type: 'DefaultCategory' },
      },
    }
  }

  // TODO-SERVER-015: RichText plugins/isInline should come from server widget.arguments
  if (modelName === 'experiments') {
    patchWidgetArgs(attributes, 'description', 'edit', {
      plugins: ['insertLink', 'embedImage', 'save'],
      isInline: false,
    })
  } else if (modelName === 'protocols') {
    patchWidgetArgs(attributes, 'description', 'edit', {
      plugins: ['insertLink', 'embedImage'],
      isInline: false,
    })
  }
}

/**
 * Per-assay-type instrument field labels.
 * Maps modelType → { fieldName: label } for instrument/instrumentmodel/instrumentrun.
 */
const INSTRUMENT_LABELS = {
  transmissionemassay: {
    instrument: 'Electron Microscope',
    instrumentrun: 'Imaging Session',
  },
  volumeemassay: {
    instrument: 'Electron Microscope',
    instrumentrun: 'Imaging Session',
  },
  lightmicroscopyassay: {
    instrumentrun: 'Imaging Session',
  },
  lightmicroscopyscreenassay: {
    instrumentrun: 'Imaging Session',
  },
  ngsilluminaassay: {
    instrumentrun: 'Sequencer Session',
  },
  avitisequencingassay: {
    instrumentrun: 'Sequencer Session',
  },
  nanoporeassay: {
    instrumentrun: 'Sequencer Session',
  },
}

function patchInstrumentLabels(modelType, attributes) {
  const labels = INSTRUMENT_LABELS[modelType?.toLowerCase()]
  if (!labels) return
  for (const [field, label] of Object.entries(labels)) {
    if (attributes[field]) {
      attributes[field] = { ...attributes[field], label }
    }
  }
}

// ---------------------------------------------------------------------------
// Global meta patches (model-level flags)
// ---------------------------------------------------------------------------

// TODO-SERVER-021: clonable should come from server global meta actions
const SERVER_CLONABLE_MODELS = new Set(['experiments', 'protocols'])

const NON_CLONABLE_MODELS = new Set([
  'datasets',
  'datasessions',
  'datafiles',
  'archives',
  'datasetcollections',
  'datafilecopies',
  'categories',
  'ordereditems',
  'ontologies',
  'terms',
  'orders',
  'workflows',
  'workflowversions',
  'workflowruns',
  'annotationtype',
])

/**
 * Patch global model metadata to add model-level capability flags.
 * Operates on `global.apps` — distinct from patchModelMeta() which patches field attributes.
 *
 * @param {Object} globalApps - The `global.apps` object from the meta store (mutated in place)
 */
export function patchGlobalMeta(globalApps) {
  for (const models of Object.values(globalApps)) {
    for (const [modelName, modelInfo] of Object.entries(models)) {
      if (!modelInfo.actions) modelInfo.actions = {}
      // Only patch if the server hasn't provided a value
      if (modelInfo.actions.clonable === undefined) {
        if (SERVER_CLONABLE_MODELS.has(modelName)) {
          modelInfo.actions.clonable = 'server'
        } else if (NON_CLONABLE_MODELS.has(modelName)) {
          modelInfo.actions.clonable = false
        } else {
          modelInfo.actions.clonable = true
        }
      }
    }
  }
}

/**
 * Build the combined model+view attributes dict and apply patchModelMeta in one step.
 * Used by both useRecordMeta (detail views) and ListComponent (list views) so patches
 * reach FieldResolver identically in every rendering context.
 *
 * @param {Array}  modelAttributes      - raw model attribute array from meta store
 * @param {Object} viewAttributesByName - view attributes keyed by field name
 * @param {string} modelName
 * @param {string} modelType
 * @returns {Object} combined dict keyed by field name, patches applied
 */
export function buildPatchedAttributes(
  modelAttributes,
  viewAttributesByName,
  modelName,
  modelType,
) {
  const combined = {}
  for (const modelAttr of modelAttributes) {
    const viewAttr = viewAttributesByName[modelAttr.name] || {}
    combined[modelAttr.name] = { ...modelAttr, ...viewAttr }
  }
  patchModelMeta(modelName, modelType, combined)
  return combined
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Deep-merge extra arguments into a field's widget config for a given mode. */
function patchWidgetArgs(attributes, fieldName, mode, extraArgs) {
  const attr = attributes[fieldName]
  if (!attr?.widget?.[mode]) return
  attributes[fieldName] = {
    ...attr,
    widget: {
      ...attr.widget,
      [mode]: {
        ...attr.widget[mode],
        arguments: { ...attr.widget[mode].arguments, ...extraArgs },
      },
    },
  }
}
